/**
 * Утилиты для работы со статусами заказов
 */

/**
 * Константы статусов заказов
 */
export const ORDER_STATUSES = {
  PENDING: 'pending',
  CONFIRMED: 'confirmed',
  PREPARING: 'preparing',
  READY: 'ready',
  DELIVERY: 'delivery',
  DELIVERED: 'delivered',
  COMPLETED: 'completed',
  CANCELLED: 'cancelled',
} as const

/**
 * Константы статусов оплаты
 */
export const PAYMENT_STATUSES = {
  PAID: 'paid',
  WAITING: 'waiting',
  FAILED: 'failed',
  PENDING: 'pending',
} as const

/**
 * Статусы завершенных заказов
 */
export const COMPLETED_STATUSES = [
  ORDER_STATUSES.CONFIRMED,
  ORDER_STATUSES.PREPARING,
  ORDER_STATUSES.READY,
  ORDER_STATUSES.DELIVERY,
  ORDER_STATUSES.DELIVERED,
  ORDER_STATUSES.COMPLETED,
] as const

/**
 * Получает текстовое описание статуса заказа
 * @param status - статус заказа
 * @returns текстовое описание
 */
export function getStatusText(status: string): string {
  switch (status) {
    case ORDER_STATUSES.PENDING: {
      return 'ожидает оплаты'
    }
    case ORDER_STATUSES.CONFIRMED: {
      return 'оплачен'
    }
    case ORDER_STATUSES.PREPARING: {
      return 'готовится'
    }
    case ORDER_STATUSES.READY: {
      return 'готов'
    }
    case ORDER_STATUSES.DELIVERY: {
      return 'в доставке'
    }
    case ORDER_STATUSES.DELIVERED: {
      return 'получен'
    }
    case ORDER_STATUSES.COMPLETED: {
      return 'выполнен'
    }
    case ORDER_STATUSES.CANCELLED: {
      return 'отменён'
    }
    default: {
      return 'обрабатывается'
    }
  }
}

/**
 * Проверяет, оплачен ли заказ или завершен
 * @param order - объект заказа
 * @returns true если заказ оплачен или завершен
 */
export function isOrderPaidOrCompleted(order: any): boolean {
  if (!order) return false

  // Если payment_status явно указывает что оплачен
  if (order.payment_status === PAYMENT_STATUSES.PAID) {
    return true
  }

  // Если заказ имеет статус выполнения - значит он точно оплачен
  return COMPLETED_STATUSES.includes(order.status)
}

/**
 * Получает текст статуса для отображения в прогрессе
 * @param order - объект заказа
 * @returns текст статуса
 */
export function getProgressStatusText(order: any): string {
  if (!order) return ''

  const isPaidOrCompleted = isOrderPaidOrCompleted(order)

  if (isPaidOrCompleted && order.status === ORDER_STATUSES.CONFIRMED) {
    return 'Оплата прошла успешно'
  }

  if (isPaidOrCompleted && order.status === ORDER_STATUSES.PREPARING) {
    return 'Заказ готовится'
  }

  if (isPaidOrCompleted && order.status === ORDER_STATUSES.READY) {
    return order.order_type === 'takeaway' ? 'Готов к самовывозу' : 'Заказ готов'
  }

  if (isPaidOrCompleted && order.status === ORDER_STATUSES.DELIVERY) {
    return 'Заказ отправлен'
  }

  if (isPaidOrCompleted && order.status === ORDER_STATUSES.DELIVERED) {
    return order.order_type === 'takeaway' ? 'Самовывоз выполнен' : 'Доставка завершена'
  }

  if (isPaidOrCompleted && order.status === ORDER_STATUSES.COMPLETED) {
    return 'Заказ выполнен'
  }

  if (order.status === ORDER_STATUSES.CANCELLED) {
    return 'Отменен'
  }

  if (!isPaidOrCompleted) {
    return 'Ждём ответа от банка'
  }

  return getStatusText(order.status)
}

/**
 * Проверяет, должна ли отображаться иконка статуса
 * @param order - объект заказа
 * @param iconType - тип иконки
 * @returns true если иконка должна быть активна
 */
export function isStatusIconActive(order: any, iconType: string): boolean {
  if (!order) return false

  const status = order.status || order.orderStatus
  const orderType = order.order_type || order.orderType

  switch (iconType) {
    case 'hourglass': {
      return !isOrderPaidOrCompleted(order) && status === ORDER_STATUSES.PENDING
    }

    case 'check': {
      return isOrderPaidOrCompleted(order) || COMPLETED_STATUSES.includes(status)
    }

    case 'bag': {
      return [ORDER_STATUSES.PREPARING, ORDER_STATUSES.READY, ORDER_STATUSES.DELIVERY, ORDER_STATUSES.DELIVERED, ORDER_STATUSES.COMPLETED].includes(status)
    }

    case 'ready': {
      return [ORDER_STATUSES.READY, ORDER_STATUSES.DELIVERY, ORDER_STATUSES.DELIVERED].includes(status)
    }

    case 'ready-pickup': {
      return orderType === 'takeaway' && [ORDER_STATUSES.READY, ORDER_STATUSES.DELIVERED].includes(status)
    }

    case 'delivery': {
      // Активна если статус - в доставке/доставлен/выполнен
      // Независимо от типа заказа (может быть takeaway со статусом delivery)
      return [ORDER_STATUSES.DELIVERY, ORDER_STATUSES.DELIVERED, ORDER_STATUSES.COMPLETED].includes(status)
    }

    case 'completed': {
      return status === ORDER_STATUSES.DELIVERED || status === ORDER_STATUSES.COMPLETED
    }

    default: {
      return false
    }
  }
}

/**
 * Получает тип заказа для отображения
 * @param order - объект заказа
 * @returns текст типа заказа
 */
export function getOrderTypeText(order: any): string {
  if (!order) return ''

  if (order.order_type === 'delivery' && order.delivery_address) {
    return `Доставка: ${order.delivery_address}`
  }

  if (order.order_type === 'takeaway') {
    return 'Самовывоз'
  }

  if (order.order_type === 'dine_in') {
    return 'В заведении'
  }

  return ''
}

/**
 * Проверяет, должен ли показываться блок повторной оплаты
 * @param order - объект заказа
 * @returns true если должен показываться
 */
export function shouldShowRetryPayment(order: any): boolean {
  if (!order) return false

  // Не показываем блок оплаты для завершенных или отмененных заказов
  if (order.status === ORDER_STATUSES.CANCELLED || order.status === ORDER_STATUSES.COMPLETED) {
    return false
  }

  return !isOrderPaidOrCompleted(order)
}

/**
 * Проверяет, должен ли показываться блок оценки
 * @param order - объект заказа
 * @param ratingSent - был ли отправлен рейтинг
 * @returns true если должен показываться
 */
export function shouldShowRating(order: any, ratingSent: boolean): boolean {
  if (!order || ratingSent) return false
  return order.status === ORDER_STATUSES.DELIVERED || order.status === ORDER_STATUSES.COMPLETED
}